import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/auth.context";

/**
 * Хук для управления видимостью пунктов сайдбара
 * Сохраняет настройки в localStorage с привязкой к userId
 */
export function useSidebarVisibility() {
  const { authState } = useAuth();
  const userId = authState.user?._id;

  const getStorageKey = useCallback(() => {
    return userId ? `sidebarVisibility_${userId}` : null;
  }, [userId]);

  // Получить сохраненные настройки видимости
  const getVisibilitySettings = useCallback((): Record<string, boolean> => {
    const storageKey = getStorageKey();
    if (!storageKey) return {};

    try {
      const saved = localStorage.getItem(storageKey);
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.error("Ошибка при чтении настроек сайдбара:", error);
      return {};
    }
  }, [getStorageKey]);

  // Сохранить настройки видимости
  const saveVisibilitySettings = useCallback(
    (settings: Record<string, boolean>) => {
      const storageKey = getStorageKey();
      if (!storageKey) return;

      try {
        localStorage.setItem(storageKey, JSON.stringify(settings));
        // Триггерим кастомное событие для обновления сайдбара в том же окне
        window.dispatchEvent(new Event("sidebarVisibilityChanged"));
      } catch (error) {
        console.error("Ошибка при сохранении настроек сайдбара:", error);
      }
    },
    [getStorageKey]
  );

  // Получить видимость конкретного пункта меню
  const isItemVisible = useCallback(
    (itemKey: string): boolean => {
      const settings = getVisibilitySettings();
      // Если настройка не задана, по умолчанию показываем (true)
      return settings[itemKey] !== undefined ? settings[itemKey] : true;
    },
    [getVisibilitySettings]
  );

  // Установить видимость пункта меню
  const setItemVisibility = useCallback(
    (itemKey: string, visible: boolean) => {
      const settings = getVisibilitySettings();
      settings[itemKey] = visible;
      saveVisibilitySettings(settings);
    },
    [getVisibilitySettings, saveVisibilitySettings]
  );

  // Установить видимость нескольких пунктов сразу
  const setItemsVisibility = useCallback(
    (items: Record<string, boolean>) => {
      const settings = getVisibilitySettings();
      Object.assign(settings, items);
      saveVisibilitySettings(settings);
    },
    [getVisibilitySettings, saveVisibilitySettings]
  );

  // Сбросить все настройки (показать все пункты)
  const resetVisibility = useCallback(() => {
    const storageKey = getStorageKey();
    if (!storageKey) return;

    try {
      localStorage.removeItem(storageKey);
      // Триггерим кастомное событие для обновления сайдбара в том же окне
      window.dispatchEvent(new Event("sidebarVisibilityChanged"));
    } catch (error) {
      console.error("Ошибка при сбросе настроек сайдбара:", error);
    }
  }, [getStorageKey]);

  // Получить все настройки
  const getAllSettings = useCallback(() => {
    return getVisibilitySettings();
  }, [getVisibilitySettings]);

  // Получить сохраненный порядок пунктов меню
  const getItemOrder = useCallback((): string[] => {
    const storageKey = getStorageKey();
    if (!storageKey) return [];

    try {
      const orderKey = `${storageKey}_order`;
      const saved = localStorage.getItem(orderKey);
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error("Ошибка при чтении порядка сайдбара:", error);
      return [];
    }
  }, [getStorageKey]);

  // Сохранить порядок пунктов меню
  const setItemOrder = useCallback((order: string[]) => {
    const storageKey = getStorageKey();
    if (!storageKey) return;

    try {
      const orderKey = `${storageKey}_order`;
      localStorage.setItem(orderKey, JSON.stringify(order));
      // Триггерим кастомное событие для обновления сайдбара
      window.dispatchEvent(new Event("sidebarOrderChanged"));
    } catch (error) {
      console.error("Ошибка при сохранении порядка сайдбара:", error);
    }
  }, [getStorageKey]);

  // Сбросить порядок (вернуть к порядку по умолчанию)
  const resetOrder = useCallback(() => {
    const storageKey = getStorageKey();
    if (!storageKey) return;

    try {
      const orderKey = `${storageKey}_order`;
      localStorage.removeItem(orderKey);
      window.dispatchEvent(new Event("sidebarOrderChanged"));
    } catch (error) {
      console.error("Ошибка при сбросе порядка сайдбара:", error);
    }
  }, [getStorageKey]);

  return {
    isItemVisible,
    setItemVisibility,
    setItemsVisibility,
    resetVisibility,
    getAllSettings,
    getItemOrder,
    setItemOrder,
    resetOrder,
  };
}

